import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { apiFetch, getJobs } from "../lib/api";
import { validateUploadFile, checkColumnMapping, remapFileHeaders, TEMPLATE_HEADERS, SAMPLE_ROW } from "../lib/validateUpload";
import Sidebar from "../components/Sidebar";
import { logEvent, logError } from "../lib/log";

const BLUE   = "#0176CE";
const ORANGE = "#F37428";

const MAX_BYTES = 50 * 1024 * 1024;

function downloadTemplate() {
  // Multi-address example: Acme Corp has two addresses (billing seq 1, remittance seq 2)
  // Beta Supply Co has one address (Address Seq blank — defaults to 1)
  // Column order: System Name, Vendor#, Name, Name2, Address Seq, Addr1-4, PO Box, City, State, ZIP, Country, Phone, Email, TaxID, Domain
  const sampleRows = [
    ['SAP', 'V-10042', 'Acme Corp', '', '1', '123 Main St Ste 400', '', '', '', '',     'Chicago', 'IL', '60601', 'US', '(312) 555-1234', 'ap@acme.com',           '36-1234567', 'acme.com'],
    ['SAP', 'V-10042', 'Acme Corp', '', '2', '',                    '', '', '', '7700', 'Chicago', 'IL', '60690', 'US', '',               'remit@acme.com',         '',           ''],
    ['SAP', 'V-20019', 'Beta Supply Co', '', '', '88 Industrial Blvd', '', '', '', '',  'Detroit', 'MI', '48201', 'US', '(313) 555-9900', 'orders@betasupply.com', '',           'betasupply.com'],
  ];
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...sampleRows]);
  ws['!cols'] = TEMPLATE_HEADERS.map((h, i) => ({
    wch: Math.max(h.length, ...sampleRows.map(r => String(r[i] || '').length)) + 4,
  }));
  XLSX.utils.book_append_sheet(wb, ws, 'Upload Template');
  XLSX.writeFile(wb, 'MasterFileAI_Template.xlsx');
}

const FIELD_SPEC = [
  { col: 1,  name: 'System Name',          req: true,  type: 'Text',  maxLen: 50,  format: 'Free text — your ERP or source system name',  example: 'SAP',                notes: 'Used to identify which ERP instance the record originates from. Multiple SAP instances = different System Names (e.g. SAP-NA, SAP-EU).' },
  { col: 2,  name: 'Vendor / Customer #',  req: true,  type: 'Text',  maxLen: 50,  format: 'Your internal ID — alphanumeric',              example: 'V-10042',            notes: 'Must be unique within a system. Combined with System Name as the unique key across uploads — used to identify new vs. existing records.' },
  { col: 3,  name: 'Vendor / Customer Name', req: true, type: 'Text', maxLen: 200, format: 'Primary legal or trade name',                  example: 'Acme Corporation',   notes: 'The most important field for enrichment — used for name matching and NAICS classification.' },
  { col: 4,  name: 'Name Line 2',          req: false, type: 'Text',  maxLen: 200, format: 'DBA / secondary name',                         example: 'Acme Corp',          notes: 'Optional. Use for doing-business-as names or alternate name references.' },
  { col: 5,  name: 'Address Seq',          req: false, type: 'Text',  maxLen: 20,  format: 'Any value that identifies the address row',    example: '2',                  notes: 'Use when a vendor has multiple addresses (billing, remittance, shipping, etc.). Repeat the same Vendor # with different seq values. Leave blank for single-address vendors — defaults to "1". Accepts text values (e.g. MAIN, REM) for systems that use non-numeric identifiers.' },
  { col: 6,  name: 'Address Line 1',       req: false, type: 'Text',  maxLen: 100, format: 'Street number and name',                       example: '123 Main St Ste 400', notes: 'Primary street address.' },
  { col: 7,  name: 'Address Line 2',       req: false, type: 'Text',  maxLen: 100, format: 'Suite, unit, floor, building',                 example: 'Suite 400',          notes: 'Secondary address information.' },
  { col: 8,  name: 'Address Line 3',       req: false, type: 'Text',  maxLen: 100, format: 'Additional address line',                      example: '',                   notes: 'Use for c/o lines or international address overflow.' },
  { col: 9,  name: 'Address Line 4',       req: false, type: 'Text',  maxLen: 100, format: 'Additional address line',                      example: '',                   notes: 'Rarely needed. Available for complex international address formats.' },
  { col: 10, name: 'PO Box',               req: false, type: 'Text',  maxLen: 50,  format: 'PO Box number only — no label needed',         example: '7700',               notes: 'If your system stores PO Box separately, put just the number here (e.g. 7700). If it\'s embedded in an address line, leave this blank — the system will extract it automatically.' },
  { col: 11, name: 'City',                 req: false, type: 'Text',  maxLen: 100, format: 'City or municipality name',                    example: 'Chicago',            notes: 'Improves address validation and geo-enrichment accuracy.' },
  { col: 12, name: 'State / Province',     req: false, type: 'Text',  maxLen: 50,  format: '2-letter state code or full name',             example: 'IL',                 notes: 'For US records use 2-letter USPS code (e.g. IL, CA, TX).' },
  { col: 13, name: 'ZIP / Postal Code',    req: false, type: 'Text',  maxLen: 20,  format: 'Any postal code format accepted',              example: '60601',              notes: 'US ZIP or ZIP+4. Canadian postal codes (M5V 3A8). International formats accepted as-is.' },
  { col: 14, name: 'Country',              req: false, type: 'Text',  maxLen: 50,  format: 'ISO 3166-1 alpha-2 preferred',                 example: 'US',                 notes: 'Strongly recommended. Use 2-letter ISO codes: US, CA, MX, GB, DE, etc.' },
  { col: 15, name: 'Phone',                req: false, type: 'Text',  maxLen: 30,  format: 'Any standard phone format',                    example: '(312) 555-1234',     notes: 'Used as a secondary matching signal. Any format accepted.' },
  { col: 16, name: 'Email',                req: false, type: 'Email', maxLen: 200, format: 'Standard email format (user@domain.tld)',      example: 'ap@acme.com',        notes: 'Used as a domain matching hint. Should be a valid email address.' },
  { col: 17, name: 'Tax ID',               req: false, type: 'Text',  maxLen: 20,  format: 'EIN, VAT, ABN, or other tax ID',              example: '36-1234567',         notes: 'US EIN format: XX-XXXXXXX. International VAT numbers also accepted.' },
  { col: 18, name: 'Website Domain',       req: false, type: 'Text',  maxLen: 100, format: 'Bare domain — no http:// or www.',             example: 'acme.com',           notes: 'Providing the domain skips AI lookup and goes straight to verification. Use bare domain only (e.g. acme.com).' },
];

function openTechSpec() {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const rows = FIELD_SPEC.map(f => `
    <tr class="${f.req ? 'req-row' : ''}">
      <td class="center">${f.col}</td>
      <td><strong>${f.name}</strong></td>
      <td class="center">${f.req ? '<span class="req-badge">Required</span>' : '<span class="opt-badge">Optional</span>'}</td>
      <td class="center">${f.type}</td>
      <td class="center">${f.maxLen}</td>
      <td>${f.format}</td>
      <td class="mono">${f.example}</td>
      <td>${f.notes}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>MasterFileAI — File Upload Specification</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10pt; color: #1e293b; background: #fff; }
  .page { max-width: 1100px; margin: 0 auto; padding: 32px 40px 60px; }

  /* Header */
  .doc-header { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 3px solid #0176CE; padding-bottom: 16px; margin-bottom: 28px; }
  .doc-logo { display: flex; align-items: center; gap: 12px; }
  .logo-mark { width: 40px; height: 40px; background: #F37428; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 20px; }
  .logo-text { color: #0176CE; font-size: 20px; font-weight: 700; letter-spacing: -0.3px; }
  .logo-text span { color: #F37428; }
  .doc-meta { text-align: right; color: #64748b; font-size: 9pt; line-height: 1.8; }
  .doc-title { font-size: 20pt; font-weight: 700; color: #0a2540; margin-bottom: 4px; }
  .doc-sub { color: #64748b; font-size: 10pt; margin-bottom: 28px; }

  /* Section */
  h2 { font-size: 12pt; font-weight: 700; color: #0176CE; text-transform: uppercase; letter-spacing: 0.06em; margin: 28px 0 10px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
  h3 { font-size: 10.5pt; font-weight: 600; color: #0a2540; margin: 16px 0 8px; }
  p  { color: #374151; font-size: 10pt; line-height: 1.7; margin-bottom: 10px; }
  ul { color: #374151; font-size: 10pt; line-height: 1.9; padding-left: 20px; margin-bottom: 10px; }
  li { margin-bottom: 2px; }

  /* Info grid */
  .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
  .info-box { background: #f8fafc; border: 1px solid #dbeafe; border-radius: 8px; padding: 12px 16px; }
  .info-box-label { color: #64748b; font-size: 8.5pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  .info-box-value { color: #0a2540; font-size: 11pt; font-weight: 700; }

  /* Field spec table */
  table { width: 100%; border-collapse: collapse; font-size: 8.5pt; margin-top: 10px; }
  thead th { background: #0176CE; color: #fff; font-weight: 600; padding: 8px 8px; text-align: left; font-size: 8pt; letter-spacing: 0.04em; }
  thead th.center { text-align: center; }
  tbody tr { border-bottom: 1px solid #e2e8f0; }
  tbody tr:hover { background: #f8fafc; }
  tbody tr.req-row { background: #fefce8; }
  tbody tr.req-row:hover { background: #fef9c3; }
  td { padding: 7px 8px; vertical-align: top; line-height: 1.5; }
  td.center { text-align: center; }
  td.mono { font-family: 'Courier New', monospace; font-size: 8pt; color: #0147a3; }
  .req-badge { background: #fef3c7; border: 1px solid #f59e0b; border-radius: 4px; color: #92400e; font-size: 7.5pt; font-weight: 700; padding: 1px 6px; white-space: nowrap; }
  .opt-badge { background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px; color: #64748b; font-size: 7.5pt; font-weight: 600; padding: 1px 6px; white-space: nowrap; }

  /* Sample row */
  .sample-block { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; margin-top: 10px; overflow-x: auto; }
  .sample-block pre { font-family: 'Courier New', monospace; font-size: 8pt; color: #0147a3; white-space: pre; line-height: 1.8; }

  /* Notes box */
  .note-box { background: #f8fafc; border-left: 4px solid #0176CE; border-radius: 0 8px 8px 0; padding: 12px 16px; margin: 16px 0; }
  .note-box.orange { border-left-color: #F37428; background: #fff7ed; }
  .note-box p { margin: 0; font-size: 9.5pt; }

  /* Footer */
  .doc-footer { margin-top: 48px; border-top: 1px solid #e2e8f0; padding-top: 14px; display: flex; justify-content: space-between; color: #94a3b8; font-size: 8.5pt; }

  @media print {
    body { font-size: 9pt; }
    .page { padding: 20px 24px 40px; max-width: 100%; }
    h2 { page-break-after: avoid; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    .no-print { display: none; }
  }
</style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="doc-header">
    <div>
      <div class="doc-logo" style="margin-bottom:10px">
        <div class="logo-mark">M</div>
        <div class="logo-text">MASTERFILE<span>AI</span></div>
      </div>
      <div class="doc-title">File Upload Specification</div>
      <div style="color:#64748b;font-size:10pt;margin-top:4px">Technical integration guide for IT / development teams</div>
    </div>
    <div class="doc-meta">
      <div><strong>Document version:</strong> 1.0</div>
      <div><strong>Date:</strong> ${today}</div>
      <div><strong>Confidential</strong></div>
    </div>
  </div>

  <!-- Overview -->
  <h2>Overview</h2>
  <p>MasterFileAI enriches vendor and customer master data records with standardized addresses, NAICS industry codes, DUNS numbers, and other business attributes. This document specifies the exact file format required to submit records for processing.</p>

  <div class="info-grid">
    <div class="info-box">
      <div class="info-box-label">Supported Formats</div>
      <div class="info-box-value">.xlsx · .csv</div>
    </div>
    <div class="info-box">
      <div class="info-box-label">Maximum File Size</div>
      <div class="info-box-value">50 MB</div>
    </div>
    <div class="info-box">
      <div class="info-box-label">Maximum Records</div>
      <div class="info-box-value">50,000 rows</div>
    </div>
    <div class="info-box">
      <div class="info-box-label">Total Columns</div>
      <div class="info-box-value">17 req + 1 optional</div>
    </div>
    <div class="info-box">
      <div class="info-box-label">Required Columns</div>
      <div class="info-box-value">3</div>
    </div>
    <div class="info-box">
      <div class="info-box-label">Free Trial</div>
      <div class="info-box-value">First 10 vendors</div>
    </div>
  </div>

  <!-- File structure -->
  <h2>File Structure Requirements</h2>
  <ul>
    <li>The file must contain <strong>17 or 18 columns</strong> in the order specified below. Column 5 (Address Seq) is optional — omit it or leave it blank for single-address vendors.</li>
    <li>Row 1 must be the <strong>header row</strong> with exact column names as specified. Column names are case-sensitive.</li>
    <li>Data begins on <strong>row 2</strong>. At least one data row is required.</li>
    <li>For CSV: use <strong>UTF-8 encoding</strong>, comma delimiter, and quote fields that contain commas or double-quotes.</li>
    <li>For XLSX: use the <strong>first worksheet</strong> only. All other sheets are ignored.</li>
    <li>Leave optional columns <strong>blank</strong> (empty cell) — do not use NULL, N/A, or placeholder values.</li>
    <li>Do not include <strong>subtotals, summary rows, or merged cells</strong>.</li>
    <li><strong>Multiple addresses per vendor:</strong> repeat the same Vendor # across rows with different Address Seq values (1, 2, 3…). Each row is one address — billing, remittance, shipping, etc.</li>
  </ul>

  <div class="note-box orange">
    <p><strong>Column order is enforced.</strong> The system reads columns by position, not by name. If columns are out of order, data will be mapped incorrectly. Always export in the exact sequence shown in the Field Specifications table below.</p>
  </div>

  <!-- Field specifications -->
  <h2>Field Specifications</h2>
  <p>Highlighted rows (<span style="background:#fef9c3;padding:1px 6px;border-radius:3px">yellow</span>) are required fields. All other fields are optional but improve enrichment accuracy.</p>

  <table>
    <thead>
      <tr>
        <th class="center" style="width:36px">#</th>
        <th style="width:160px">Column Name</th>
        <th class="center" style="width:72px">Required</th>
        <th class="center" style="width:52px">Type</th>
        <th class="center" style="width:52px">Max Len</th>
        <th style="width:160px">Format / Valid Values</th>
        <th style="width:100px">Example</th>
        <th>Notes</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <!-- Sample records -->
  <h2>Sample Records</h2>
  <p>The following shows a valid CSV with two vendors — Acme Corp has two address rows (billing seq 1, remittance seq 2 with PO Box), Beta Supply Co has one address (Address Seq blank). All fields are quoted for safe CSV handling.</p>
  <div class="sample-block">
    <pre>"System Name","Vendor / Customer #","Vendor / Customer Name","Name Line 2","Address Seq","Address Line 1","Address Line 2","Address Line 3","Address Line 4","PO Box","City","State / Province","ZIP / Postal Code","Country","Phone","Email","Tax ID","Website Domain"
"SAP","V-10042","Acme Corp","","1","123 Main St Ste 400","","","","","Chicago","IL","60601","US","(312) 555-1234","ap@acme.com","36-1234567","acme.com"
"SAP","V-10042","Acme Corp","","2","","","","","7700","Chicago","IL","60690","US","","remit@acme.com","",""
"SAP","V-20019","Beta Supply Co","","","88 Industrial Blvd","","","","","Detroit","MI","48201","US","(313) 555-9900","orders@betasupply.com","","betasupply.com"</pre>
  </div>
  <p style="margin-top:10px;font-size:9.5pt;color:#64748b">Acme Corp rows share the same Vendor # (V-10042) — both addresses are enriched together and billed as one vendor.</p>

  <!-- Integration notes -->
  <h2>Integration Notes</h2>

  <h3>Exporting from SAP</h3>
  <ul>
    <li>Use transaction <strong>FK03 / XK03</strong> for vendor master data export.</li>
    <li>Map fields: Lifnr → Vendor/Customer #, Name1 → Vendor/Customer Name, Stras → Address Line 1, Ort01 → City, Regio → State, Pstlz → ZIP, Land1 → Country, Stcd1 → Tax ID.</li>
    <li>Export via <strong>SE16N</strong> on table LFA1 (general data) and LFB1 (company code data) for a complete extract.</li>
  </ul>

  <h3>Exporting from Oracle / NetSuite</h3>
  <ul>
    <li>Use Saved Search or CSV export from the Vendor / Customer center.</li>
    <li>Map: Internal ID → Vendor/Customer #, Company Name → Vendor/Customer Name.</li>
    <li>For NetSuite SuiteAnalytics: query <strong>transaction_lines</strong> or <strong>vendor</strong> record type.</li>
  </ul>

  <h3>Exporting from Dynamics 365</h3>
  <ul>
    <li>Use <strong>Data Management → Export</strong> or the Vendor entity from the Data entities workspace.</li>
    <li>Map: VendAccount → Vendor/Customer #, VendName → Vendor/Customer Name.</li>
  </ul>

  <h3>General scripting guidance</h3>
  <ul>
    <li>Concatenate all source address fields into Address Line 1–4 as appropriate.</li>
    <li>Strip leading/trailing whitespace from all fields before export.</li>
    <li>Remove any characters that are not printable UTF-8 (control characters, BOM markers).</li>
    <li>For Tax ID: remove formatting dashes only if your system stores them separately; MasterFileAI accepts formatted values (e.g. 36-1234567).</li>
    <li>Website Domain: if your system stores full URLs, extract just the domain using a regex: <span style="font-family:monospace;font-size:8pt">([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,}</span></li>
  </ul>

  <div class="note-box">
    <p><strong>Tip:</strong> Provide as many optional fields as your system has available. Each additional field increases enrichment accuracy — especially City, State, ZIP, Country, and Tax ID.</p>
  </div>

  <!-- Validation rules -->
  <h2>Validation Rules</h2>
  <p>The MasterFileAI portal validates your file before upload and will report specific row-level errors. The following checks are enforced:</p>
  <ul>
    <li><strong>System Name</strong> — cannot be blank.</li>
    <li><strong>Vendor / Customer #</strong> — cannot be blank.</li>
    <li><strong>Vendor / Customer Name</strong> — cannot be blank.</li>
    <li><strong>Email</strong> — if provided, should be a valid email address (user@domain.tld). Invalid formats are flagged as a warning.</li>
    <li><strong>ZIP / Postal Code</strong> — any format accepted. Passed through as-is for AI standardization.</li>
    <li><strong>Website Domain</strong> — if provided, use the bare domain (e.g. acme.com). Including "http://" or "www." is flagged as a warning — the system will strip it but it is better to provide the clean value.</li>
    <li><strong>Address Seq</strong> — if provided, any non-blank text value is accepted (numbers, codes, labels). Leave blank for single-address vendors.</li>
  </ul>

  <!-- Footer -->
  <div class="doc-footer">
    <div>MasterFileAI · File Upload Specification v1.0 · masterfileai.com</div>
    <div>Generated ${today} · Confidential</div>
  </div>

  <div class="no-print" style="margin-top:32px;text-align:center">
    <button onclick="window.print()" style="background:#F37428;border:none;border-radius:8px;color:#fff;cursor:pointer;font-size:14px;font-weight:600;padding:12px 28px">
      Print / Save as PDF
    </button>
    <p style="color:#94a3b8;font-size:12px;margin-top:10px">Use your browser's Print dialog and choose "Save as PDF" as the destination.</p>
  </div>

</div>
</body>
</html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
}

export default function UploadFile() {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => { logEvent('page_view', { page: 'upload' }); }, []);

  const [pendingJob, setPendingJob] = useState(null);
  useEffect(() => {
    getJobs().then(res => {
      const active = (res.jobs || []).find(j => j.status === 'uploaded' || j.status === 'pending');
      if (active) setPendingJob(active);
    }).catch(() => {});
  }, []);

  const [file, setFile]               = useState(null);
  const [remappedFile, setRemappedFile] = useState(null);
  const [mappingInfo, setMappingInfo] = useState(null);   // { userHeaders, rowCount } when headers differ
  const [dragOver, setDragOver]       = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadWarn, setUploadWarn]   = useState('');
  const [validating, setValidating]   = useState(false);
  const [validation, setValidation]   = useState(null);

  function checkSize(f) {
    if (!f) return 'No file selected.';
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'xlsm', 'xlsb', 'csv'].includes(ext)) return 'Only .xlsx, .xls, and .csv files are accepted.';
    if (f.size > MAX_BYTES) return 'File is too large. Maximum size is 50 MB.';
    return null;
  }

  async function processFile(f) {
    const sizeErr = checkSize(f);
    if (sizeErr) {
      setValidation({ valid: false, errors: [sizeErr], warnings: [], rowCount: 0 });
      logEvent('upload_failed', { stage: 'size_check', error: sizeErr, filename: f.name });
      return;
    }
    setFile(f); setValidation(null); setMappingInfo(null); setRemappedFile(null);
    setUploadError(''); setUploadWarn('');

    // Check if headers match exactly — if not, show mapping approval screen
    const mapping = await checkColumnMapping(f);
    if (mapping && !mapping.exact) {
      setMappingInfo({ userHeaders: mapping.userHeaders, rowCount: mapping.rowCount });
      return;
    }

    setValidating(true);
    const result = await validateUploadFile(f);
    setValidating(false); setValidation(result);
    if (!result.valid) {
      logEvent('upload_failed', { stage: 'validation', errors: result.errors, filename: f.name, row_count: result.rowCount });
    } else {
      logEvent('upload_validated', { filename: f.name, row_count: result.rowCount });
    }
  }

  async function handleMappingApprove() {
    setMappingInfo(null); setValidating(true);
    const rf = await remapFileHeaders(file);
    setRemappedFile(rf);
    const result = await validateUploadFile(rf);
    setValidating(false); setValidation(result);
    if (!result.valid) {
      logEvent('upload_failed', { stage: 'validation_remapped', filename: file.name });
    } else {
      logEvent('upload_validated', { filename: file.name, row_count: result.rowCount, remapped: true });
    }
  }

  function handleMappingCancel() {
    setFile(null); setMappingInfo(null); setRemappedFile(null);
    setValidation(null); setUploadError(''); setUploadWarn(''); setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processFile(f);
  }, []);

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (f) processFile(f);
  }

  function removeFile() {
    setFile(null); setValidation(null); setMappingInfo(null); setRemappedFile(null);
    setUploadError(''); setUploadWarn(''); setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function handleUpload() {
    if (!file || !validation?.valid) return;
    setUploadError(''); setUploadWarn(''); setUploading(true); setProgress(10);
    try {
      const uploadFile = remappedFile || file;
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(uploadFile);
      });
      setProgress(40);
      const res = await apiFetch('/api/upload', {
        method: 'POST',
        body: { filename: file.name, content_type: file.type || 'application/octet-stream', file_data: base64 },
      });
      setProgress(100);
      if (res.job_id) {
        logEvent('upload_success', { filename: file.name, job_id: res.job_id, row_count: validation?.rowCount, truncations: res.truncation_warnings?.length ?? 0 });
        const truncations = res.truncation_warnings || [];
        if (truncations.length > 0) {
          const fields = [...new Set(truncations.map(w => w.field))].join(', ');
          setUploadWarn(`${truncations.length} value(s) were trimmed to fit field length limits (affected: ${fields}). Your file will still process normally — redirecting…`);
          setTimeout(() => navigate(`/process/${res.job_id}`, { replace: true }), 4000);
        } else {
          setTimeout(() => navigate(`/process/${res.job_id}`, { replace: true }), 400);
        }
      } else {
        setUploadError(res.message || 'Upload failed. Please try again.');
        logEvent('upload_failed', { stage: 'api', error: res.message, filename: file.name });
        setUploading(false); setProgress(0);
      }
    } catch (err) {
      logError('upload', err);
      setUploadError(err.message || 'Network error. Please try again.');
      setUploading(false); setProgress(0);
    }
  }

  function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  const canUpload = file && validation?.valid && !uploading && !mappingInfo;

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
        <Sidebar active="upload" />

        <main style={styles.main}>
          <div style={styles.header}>
            <div>
              <h1 style={styles.pageTitle}>Upload a file</h1>
              <p style={styles.pageSub}>Upload your vendor or customer master data to start cleaning.</p>
            </div>
          </div>

          {pendingJob && (
            <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#92400e', fontSize: 14 }}>You have an unprocessed file</div>
                <div style={{ color: '#78350f', fontSize: 13, marginTop: 2 }}>{pendingJob.file_name} · {(pendingJob.record_count || 0).toLocaleString()} records</div>
              </div>
              <button
                onClick={() => navigate(`/process/${pendingJob.job_id}`)}
                style={{ background: '#f59e0b', border: 'none', borderRadius: 7, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, padding: '8px 18px', whiteSpace: 'nowrap' }}
              >
                Resume →
              </button>
            </div>
          )}

          <div style={styles.content}>
            {/* Template download */}
            <div style={styles.templateBox}>
              <div style={styles.templateLeft}>
                <div style={styles.templateTitle}>Start with the MasterFileAI template</div>
                <div style={styles.templateSub}>16 required + 1 optional · includes multi-address example · works with Excel or any spreadsheet app</div>
              </div>
              <div style={styles.templateBtns}>
                <button style={styles.dlBtn} onClick={downloadTemplate}>⬇ Download .xlsx</button>
                <button style={styles.dlBtnSecondary} onClick={openTechSpec}>📄 Technical Spec (PDF)</button>
              </div>
            </div>

            {/* Requirements */}
            <div style={styles.callout}>
              <div style={styles.calloutTitle}>Required columns</div>
              <div style={styles.reqCols}>
                {['System Name', 'Vendor / Customer #', 'Vendor / Customer Name'].map(c => (
                  <span key={c} style={styles.reqBadge}>{c}</span>
                ))}
              </div>
              <div style={{ ...styles.calloutTitle, marginTop: 10 }}>File requirements</div>
              <ul style={styles.calloutList}>
                <li>.xlsx, .xls, or .csv format, maximum 50 MB</li>
                <li>Up to 50,000 rows per file</li>
                <li>First 10 vendors always processed free — no credit card required</li>
                <li>Multiple addresses per vendor? Add the optional <strong>Address Seq</strong> column (column 17)</li>
              </ul>
            </div>

            {/* Drop zone */}
            <div
              style={{
                ...styles.dropZone,
                ...(dragOver ? styles.dropZoneActive : {}),
                ...(file ? styles.dropZoneHasFile : {}),
              }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !file && inputRef.current?.click()}
            >
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.xlsm,.xlsb,.csv" style={{ display: 'none' }} onChange={handleFileChange} />

              {!file ? (
                <div style={styles.dropInner}>
                  <div style={styles.dropIcon}>📂</div>
                  <p style={styles.dropPrimary}>Drag & drop your file here</p>
                  <p style={styles.dropSecondary}>or click to browse</p>
                  <div style={styles.acceptedBadges}>
                    {['.xlsx', '.csv'].map(ext => <span key={ext} style={styles.extBadge}>{ext}</span>)}
                  </div>
                </div>
              ) : (
                <div style={styles.filePreview}>
                  <div style={styles.fileIcon}>{file.name.endsWith('.xlsx') ? '📊' : '📄'}</div>
                  <div style={styles.fileMeta}>
                    <div style={styles.fileName}>{file.name}</div>
                    <div style={styles.fileSize}>{formatSize(file.size)}</div>
                  </div>
                  <button style={styles.removeBtn} onClick={e => { e.stopPropagation(); removeFile(); }} disabled={uploading}>✕</button>
                </div>
              )}
            </div>

            {/* Column mapping approval */}
            {mappingInfo && (
              <MappingScreen
                filename={file?.name}
                userHeaders={mappingInfo.userHeaders}
                rowCount={mappingInfo.rowCount}
                onApprove={handleMappingApprove}
                onCancel={handleMappingCancel}
              />
            )}

            {/* Validation */}
            {validating && (
              <div style={styles.validatingBox}>
                <span>⏳</span> Checking your file…
              </div>
            )}
            {validation && !validating && <ValidationPanel v={validation} />}

            {/* Upload warning (truncation) */}
            {uploadWarn && <div style={styles.uploadWarnBox}>{uploadWarn}</div>}

            {/* Upload error */}
            {uploadError && <div style={styles.uploadErrorBox}>{uploadError}</div>}

            {/* Progress */}
            {uploading && (
              <div style={styles.progressWrap}>
                <div style={styles.progressTrack}>
                  <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                </div>
                <span style={styles.progressLabel}>
                  {progress < 40 ? 'Reading file…' : progress < 90 ? 'Uploading…' : 'Almost done…'}
                </span>
              </div>
            )}

            {/* Actions */}
            <div style={styles.actions}>
              <Link to="/dashboard" style={{ textDecoration: 'none' }}>
                <button style={styles.cancelBtn} disabled={uploading}>Cancel</button>
              </Link>
              <button
                style={{ ...styles.uploadBtn, opacity: canUpload ? 1 : 0.4, cursor: canUpload ? 'pointer' : 'not-allowed' }}
                disabled={!canUpload}
                onClick={handleUpload}
              >
                {uploading ? 'Uploading…' : 'Upload file →'}
              </button>
            </div>

            <p style={styles.freeNote}>🎁 Your first 10 records are always free — no credit card needed to get started.</p>
          </div>
        </main>
      </div>
    </div>
  );
}

const REQUIRED_COLS = new Set(['System Name', 'Vendor / Customer #', 'Vendor / Customer Name']);

function MappingScreen({ filename, userHeaders, rowCount, onApprove, onCancel }) {
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
      <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ fontSize: 18 }}>⚠</span>
        <div>
          <div style={{ fontWeight: 600, color: '#92400e', fontSize: 14, marginBottom: 2 }}>Column headers don't match our template</div>
          <div style={{ color: '#78350f', fontSize: 13 }}>
            <strong>{filename}</strong> · {rowCount.toLocaleString()} records · We'll load each column by its position (column 1 = System Name, column 2 = Vendor #, etc.). Review the mapping below and approve to continue.
          </div>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', padding: '7px 14px', textAlign: 'left', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', width: 40 }}>Col</th>
            <th style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', padding: '7px 14px', textAlign: 'left', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Your Header</th>
            <th style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', padding: '7px 14px', textAlign: 'left', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Will load as</th>
            <th style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', padding: '7px 14px', textAlign: 'left', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {TEMPLATE_HEADERS.map((tplHeader, i) => {
            const userHeader = userHeaders[i] || '';
            const required   = REQUIRED_COLS.has(tplHeader);
            const missing    = required && !userHeader;
            return (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: missing ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                <td style={{ color: '#9ca3af', fontSize: 12, padding: '8px 14px', textAlign: 'center' }}>{i + 1}</td>
                <td style={{ color: userHeader ? '#374151' : '#d1d5db', fontFamily: userHeader ? 'monospace' : 'inherit', fontSize: 12, padding: '8px 14px', fontStyle: userHeader ? 'normal' : 'italic' }}>
                  {userHeader || '(no column)'}
                </td>
                <td style={{ color: '#111827', fontSize: 13, fontWeight: 500, padding: '8px 14px' }}>{tplHeader}</td>
                <td style={{ padding: '8px 14px' }}>
                  {required && (
                    <span style={{ background: missing ? 'rgba(239,68,68,0.1)' : 'rgba(1,118,206,0.1)', borderRadius: 4, color: missing ? '#dc2626' : BLUE, fontSize: 10, fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {missing ? 'Missing!' : 'Required'}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '12px 16px' }}>
        <button onClick={onCancel} style={{ background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 7, color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '8px 16px' }}>
          Cancel
        </button>
        <button onClick={onApprove} style={{ background: BLUE, border: 'none', borderRadius: 7, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 20px' }}>
          Looks right — proceed →
        </button>
      </div>
    </div>
  );
}

function ValidationPanel({ v }) {
  const { structureErrors, fields, rowCount, totalErrors, totalWarnings, valid } = v;

  if (structureErrors.length > 0) {
    return (
      <div style={vStyles.errorBox}>
        <div style={vStyles.errorTitle}>⚠ Can't read this file</div>
        <ul style={vStyles.simpleList}>
          {structureErrors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      </div>
    );
  }

  if (!fields.length) return null;
  const hasIssues = totalErrors > 0 || totalWarnings > 0;

  return (
    <div style={vStyles.panel}>
      <div style={vStyles.summaryBar}>
        <span style={vStyles.rowCount}>{rowCount.toLocaleString()} record{rowCount !== 1 ? 's' : ''}</span>
        {valid && totalWarnings === 0 && <span style={vStyles.allGood}>✓ All fields look good</span>}
        {totalErrors > 0 && <span style={vStyles.badgeError}>{totalErrors.toLocaleString()} error{totalErrors !== 1 ? 's' : ''} — fix before uploading</span>}
        {totalWarnings > 0 && <span style={vStyles.badgeWarn}>{totalWarnings.toLocaleString()} warning{totalWarnings !== 1 ? 's' : ''} — won't block upload</span>}
      </div>
      {hasIssues && (
        <table style={vStyles.table}>
          <thead>
            <tr>
              <th style={vStyles.th}>Field</th>
              <th style={{ ...vStyles.th, width: 90 }}>Status</th>
              <th style={vStyles.th}>Affected rows</th>
            </tr>
          </thead>
          <tbody>
            {fields.map(f => {
              if (f.status === 'ok' || f.status === 'unchecked') return null;
              const isErr = f.status === 'error';
              const pct = Math.round((f.count / rowCount) * 100);
              const exampleText = f.examples.length > 0
                ? 'e.g. row' + (f.examples.length > 1 ? 's' : '') + ' ' + f.examples.join(', ') +
                  (f.count > f.examples.length ? ` (+${(f.count - f.examples.length).toLocaleString()} more)` : '')
                : '';
              return (
                <tr key={f.name} style={vStyles.tr}>
                  <td style={vStyles.tdField}>{f.name}{f.required && <span style={vStyles.reqTag}>required</span>}</td>
                  <td style={vStyles.tdStatus}>
                    <span style={isErr ? vStyles.errChip : vStyles.warnChip}>
                      {isErr ? '✗' : '⚠'} {f.count.toLocaleString()} row{f.count !== 1 ? 's' : ''}{rowCount > 0 ? ` (${pct}%)` : ''}
                    </span>
                  </td>
                  <td style={vStyles.tdExamples}>
                    {exampleText && <span style={vStyles.exampleText}>{exampleText}</span>}
                    {f.sampleMessage && <span style={vStyles.sampleMsg}> · {f.sampleMessage}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {hasIssues && (() => {
        const cleanCount = fields.filter(f => f.status === 'ok').length;
        const unchecked  = fields.filter(f => f.status === 'unchecked').length;
        return (
          <div style={vStyles.cleanNote}>
            {cleanCount} of 17 field{cleanCount !== 1 ? 's' : ''} look clean
            {unchecked > 0 ? ` · ${unchecked} optional field${unchecked !== 1 ? 's' : ''} not checked` : ''}
          </div>
        );
      })()}
    </div>
  );
}

const vStyles = {
  panel:      { border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 },
  summaryBar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  rowCount:   { color: '#374151', fontSize: 13, fontWeight: 600 },
  allGood:    { color: '#059669', fontSize: 13, fontWeight: 500 },
  badgeError: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 5, color: '#dc2626', fontSize: 12, fontWeight: 600, padding: '2px 8px' },
  badgeWarn:  { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 5, color: '#b45309', fontSize: 12, fontWeight: 600, padding: '2px 8px' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th:         { background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', padding: '7px 14px', textAlign: 'left', textTransform: 'uppercase' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  tdField:    { color: '#111827', fontSize: 13, fontWeight: 500, padding: '9px 14px', whiteSpace: 'nowrap' },
  tdStatus:   { padding: '9px 14px', whiteSpace: 'nowrap' },
  tdExamples: { color: '#64748b', fontSize: 12, padding: '9px 14px' },
  reqTag:     { background: `rgba(1,118,206,0.1)`, borderRadius: 4, color: BLUE, fontSize: 10, fontWeight: 600, marginLeft: 6, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.04em' },
  errChip:    { background: 'rgba(239,68,68,0.1)', borderRadius: 5, color: '#dc2626', fontSize: 12, fontWeight: 600, padding: '2px 7px' },
  warnChip:   { background: 'rgba(245,158,11,0.1)', borderRadius: 5, color: '#b45309', fontSize: 12, fontWeight: 600, padding: '2px 7px' },
  exampleText:{ color: '#64748b' },
  sampleMsg:  { color: '#9ca3af', fontStyle: 'italic' },
  cleanNote:  { background: '#f8fafc', borderTop: '1px solid #e2e8f0', color: '#64748b', fontSize: 12, padding: '8px 14px' },
  errorBox:   { background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 14 },
  errorTitle: { color: '#dc2626', fontSize: 13, fontWeight: 600, marginBottom: 6 },
  simpleList: { color: '#374151', fontSize: 13, lineHeight: 1.8, margin: 0, paddingLeft: 18 },
};

const styles = {
  page:            { display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe:       { height: 4, background: `linear-gradient(90deg, ${BLUE}, ${ORANGE})`, flexShrink: 0 },
  body:            { display: 'flex', flex: 1 },
  main:            { flex: 1, background: '#f8fafc', overflowY: 'auto', padding: '40px 48px' },
  header:          { marginBottom: 28 },
  pageTitle:       { color: '#0f172a', fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: '-0.5px' },
  pageSub:         { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  content:         { maxWidth: 680 },
  templateBox:     { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  templateLeft:    { flex: 1, minWidth: 200 },
  templateTitle:   { color: '#0f172a', fontWeight: 600, fontSize: 14, marginBottom: 3 },
  templateSub:     { color: '#64748b', fontSize: 12 },
  templateBtns:    { display: 'flex', gap: 8, flexShrink: 0 },
  dlBtn:           { background: ORANGE, border: 'none', borderRadius: 7, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 16px', whiteSpace: 'nowrap' },
  dlBtnSecondary:  { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, color: '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '8px 16px', whiteSpace: 'nowrap' },
  callout:         { background: `rgba(1,118,206,0.05)`, border: `1px solid rgba(1,118,206,0.2)`, borderRadius: 10, padding: '14px 18px', marginBottom: 24 },
  calloutTitle:    { color: BLUE, fontWeight: 600, fontSize: 11, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' },
  reqCols:         { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 },
  reqBadge:        { background: `rgba(1,118,206,0.1)`, border: `1px solid rgba(1,118,206,0.25)`, borderRadius: 5, color: '#0147a3', fontSize: 12, fontWeight: 500, padding: '2px 8px' },
  calloutList:     { color: '#374151', fontSize: 13, lineHeight: 1.8, margin: 0, paddingLeft: 18 },
  dropZone:        { border: '2px dashed #c8d8e8', borderRadius: 14, cursor: 'pointer', minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.2s, background 0.2s', background: '#fff', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  dropZoneActive:  { borderColor: BLUE, background: 'rgba(1,118,206,0.03)' },
  dropZoneHasFile: { borderStyle: 'solid', borderColor: '#e2e8f0', cursor: 'default' },
  dropInner:       { textAlign: 'center', padding: '28px 24px' },
  dropIcon:        { fontSize: 36, marginBottom: 10 },
  dropPrimary:     { color: '#0f172a', fontSize: 15, fontWeight: 600, margin: '0 0 4px' },
  dropSecondary:   { color: '#64748b', fontSize: 13, margin: '0 0 14px' },
  acceptedBadges:  { display: 'flex', gap: 8, justifyContent: 'center' },
  extBadge:        { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, color: '#64748b', fontSize: 12, fontWeight: 500, padding: '3px 10px' },
  filePreview:     { display: 'flex', alignItems: 'center', gap: 14, padding: '20px 24px', width: '100%', boxSizing: 'border-box' },
  fileIcon:        { fontSize: 30, flexShrink: 0 },
  fileMeta:        { flex: 1, overflow: 'hidden' },
  fileName:        { color: '#0f172a', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize:        { color: '#64748b', fontSize: 12, marginTop: 2 },
  removeBtn:       { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 6, color: '#64748b', cursor: 'pointer', fontSize: 13, padding: '4px 10px', flexShrink: 0 },
  validatingBox:   { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', fontSize: 13, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 },
  uploadErrorBox:  { background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 12, color: '#dc2626', fontSize: 13 },
  uploadWarnBox:   { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 8, padding: '12px 16px', marginBottom: 12, color: '#92400e', fontSize: 13 },
  progressWrap:    { marginBottom: 16 },
  progressTrack:   { background: '#e2e8f0', borderRadius: 4, height: 6, overflow: 'hidden', marginBottom: 6 },
  progressFill:    { background: `linear-gradient(90deg, ${BLUE}, ${ORANGE})`, height: '100%', borderRadius: 4, transition: 'width 0.4s ease' },
  progressLabel:   { color: '#64748b', fontSize: 12 },
  actions:         { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 },
  cancelBtn:       { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 14, fontWeight: 500, padding: '11px 22px' },
  uploadBtn:       { background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, padding: '11px 28px' },
  freeNote:        { color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 24 },
};
